'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import type { ManualCompetitor } from './CompetitorLocationsManager';

// ─── Types ────────────────────────────────────────────────────────────────────
export type ThreatTier = 'critical' | 'high' | 'medium' | 'low';

export interface ThreatAlert {
  id: string;
  competitor_id: string;
  competitor_name: string;
  city: string;
  state: string;
  tier: ThreatTier;
  message: string;
  triggered_at: string;
  dismissed: boolean;
}

interface RankedCompetitor extends ManualCompetitor {
  threatTier: ThreatTier;
  threatScore: number;
  threatFactors: string[];
}

interface ThreatTierSystemProps {
  competitors: ManualCompetitor[];
}

// ─── Tier config ──────────────────────────────────────────────────────────────
const TIER_CONFIG: Record<ThreatTier, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  badge: string;
  description: string;
}> = {
  critical: {
    label: 'Critical Threat',
    color: '#DC2626',
    bg: '#FEF2F2',
    border: '#FECACA',
    icon: 'ExclamationCircleIcon',
    badge: '#DC2626',
    description: 'Dominant regional player — immediate strategic response required',
  },
  high: {
    label: 'High Threat',
    color: '#EA580C',
    bg: '#FFF7ED',
    border: '#FED7AA',
    icon: 'ExclamationTriangleIcon',
    badge: '#EA580C',
    description: 'Significant local presence — monitor closely and plan counter-strategy',
  },
  medium: {
    label: 'Medium Threat',
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FDE68A',
    icon: 'ShieldExclamationIcon',
    badge: '#D97706',
    description: 'Established competitor — track growth and market share changes',
  },
  low: {
    label: 'Low Threat',
    color: '#059669',
    bg: '#F0FDF4',
    border: '#BBF7D0',
    icon: 'ShieldCheckIcon',
    badge: '#059669',
    description: 'Limited market impact — routine monitoring sufficient',
  },
};

// ─── Scoring engine ───────────────────────────────────────────────────────────
function computeThreatScore(c: ManualCompetitor): { score: number; factors: string[] } {
  let score = 0;
  const factors: string[] = [];

  // Size (0–40 pts)
  if (c.size === 'large') {
    score += 40;
    factors.push('Large regional operator (50+ crews)');
  } else if (c.size === 'medium') {
    score += 22;
    factors.push('Mid-size local presence (10–50 crews)');
  } else {
    score += 8;
    factors.push('Small neighborhood operator');
  }

  // Rating / review count → market penetration proxy (0–20 pts)
  if (c.rating && c.rating >= 4.5) {
    score += 20;
    factors.push(`High customer satisfaction (${c.rating}★)`);
  } else if (c.rating && c.rating >= 4.0) {
    score += 12;
    factors.push(`Good reputation (${c.rating}★)`);
  } else if (c.rating && c.rating >= 3.5) {
    score += 6;
    factors.push(`Average reputation (${c.rating}★)`);
  }

  if (c.reviewCount && c.reviewCount >= 500) {
    score += 15;
    factors.push(`High review volume (${c.reviewCount} reviews)`);
  } else if (c.reviewCount && c.reviewCount >= 100) {
    score += 8;
    factors.push(`Moderate review volume (${c.reviewCount} reviews)`);
  } else if (c.reviewCount && c.reviewCount >= 20) {
    score += 3;
  }

  // Market share (0–15 pts)
  if (c.marketShare) {
    const pct = parseFloat(c.marketShare);
    if (!isNaN(pct)) {
      if (pct >= 20) { score += 15; factors.push(`Dominant market share (${pct}%)`); }
      else if (pct >= 10) { score += 9; factors.push(`Significant market share (${pct}%)`); }
      else if (pct >= 5) { score += 5; factors.push(`Moderate market share (${pct}%)`); }
    }
  }

  // Years in business → longevity / brand equity (0–10 pts)
  if (c.yearsInBusiness) {
    const yrs = parseInt(c.yearsInBusiness);
    if (!isNaN(yrs)) {
      if (yrs >= 15) { score += 10; factors.push(`Long-established brand (${yrs} yrs)`); }
      else if (yrs >= 7) { score += 6; factors.push(`Established operator (${yrs} yrs)`); }
      else if (yrs >= 3) { score += 3; }
    }
  }

  // Web presence (0–5 pts)
  if (c.website) {
    score += 5;
    factors.push('Active web presence');
  }

  // Annual revenue (0–10 pts)
  if (c.annualRevenue) {
    const rev = parseFloat(c.annualRevenue.replace(/[^0-9.]/g, ''));
    if (!isNaN(rev)) {
      if (rev >= 5_000_000) { score += 10; factors.push('High annual revenue ($5M+)'); }
      else if (rev >= 1_000_000) { score += 6; factors.push('Significant annual revenue ($1M+)'); }
      else if (rev >= 500_000) { score += 3; }
    }
  }

  return { score: Math.min(score, 100), factors };
}

function scoreTier(score: number): ThreatTier {
  if (score >= 70) return 'critical';
  if (score >= 45) return 'high';
  if (score >= 22) return 'medium';
  return 'low';
}

function rankCompetitors(competitors: ManualCompetitor[]): RankedCompetitor[] {
  return competitors
    .map((c) => {
      const { score, factors } = computeThreatScore(c);
      return { ...c, threatScore: score, threatTier: scoreTier(score), threatFactors: factors };
    })
    .sort((a, b) => b.threatScore - a.threatScore);
}

// ─── Alert Banner ─────────────────────────────────────────────────────────────
interface AlertBannerProps {
  alerts: ThreatAlert[];
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
}

function AlertBanner({ alerts, onDismiss, onDismissAll }: AlertBannerProps) {
  const active = alerts.filter((a) => !a.dismissed);
  if (active.length === 0) return null;

  return (
    <div
      className="rounded-2xl overflow-hidden mb-6"
      style={{ border: '2px solid #DC2626', background: '#FEF2F2' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-sm font-bold text-white tracking-wide uppercase">
            🚨 Red Alert — {active.length} High-Threat {active.length === 1 ? 'Competitor' : 'Competitors'} Detected
          </span>
        </div>
        <button
          onClick={onDismissAll}
          className="text-xs font-semibold text-red-100 hover:text-white transition-colors px-3 py-1 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.15)' }}
        >
          Dismiss All
        </button>
      </div>

      {/* Alert rows */}
      <div className="divide-y divide-red-100">
        {active.map((alert) => (
          <div key={alert.id} className="flex items-start gap-3 px-5 py-3">
            <div
              className="mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: TIER_CONFIG[alert.tier].badge, opacity: 0.9 }}
            >
              <Icon name="ExclamationCircleIcon" size={14} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: '#991B1B' }}>
                {alert.competitor_name}
                <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                  {alert.city}, {alert.state}
                </span>
              </p>
              <p className="text-xs mt-0.5" style={{ color: '#B91C1C' }}>{alert.message}</p>
            </div>
            <button
              onClick={() => onDismiss(alert.id)}
              className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center hover:bg-red-200 transition-colors"
              title="Dismiss"
            >
              <Icon name="XMarkIcon" size={12} className="text-red-400" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tier Badge ───────────────────────────────────────────────────────────────
function TierBadge({ tier }: { tier: ThreatTier }) {
  const cfg = TIER_CONFIG[tier];
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <Icon name={cfg.icon} size={11} />
      {cfg.label}
    </span>
  );
}

// ─── Score Bar ────────────────────────────────────────────────────────────────
function ScoreBar({ score, tier }: { score: number; tier: ThreatTier }) {
  const cfg = TIER_CONFIG[tier];
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${score}%`, background: cfg.color }}
        />
      </div>
      <span className="text-xs font-black w-8 text-right" style={{ color: cfg.color }}>
        {score}
      </span>
    </div>
  );
}

// ─── Tier Summary Strip ───────────────────────────────────────────────────────
function TierSummaryStrip({ ranked }: { ranked: RankedCompetitor[] }) {
  const counts: Record<ThreatTier, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  ranked.forEach((c) => counts[c.threatTier]++);

  return (
    <div className="grid grid-cols-4 gap-3 mb-6">
      {(['critical', 'high', 'medium', 'low'] as ThreatTier[]).map((tier) => {
        const cfg = TIER_CONFIG[tier];
        return (
          <div
            key={tier}
            className="rounded-xl p-3 text-center"
            style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
          >
            <div className="text-2xl font-black" style={{ color: cfg.color }}>{counts[tier]}</div>
            <div className="text-xs font-semibold mt-0.5" style={{ color: cfg.color }}>{cfg.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ThreatTierSystem({ competitors }: ThreatTierSystemProps) {
  const [ranked, setRanked] = useState<RankedCompetitor[]>([]);
  const [alerts, setAlerts] = useState<ThreatAlert[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterTier, setFilterTier] = useState<ThreatTier | 'all'>('all');
  const [savingAlerts, setSavingAlerts] = useState(false);
  const prevIdsRef = useRef<Set<string>>(new Set());
  const supabase = createClient();

  // ─── Load persisted dismissed alerts from Supabase ────────────────────────
  useEffect(() => {
    const loadAlerts = async () => {
      const { data } = await supabase
        .from('expansion_threat_alerts')
        .select('*')
        .order('triggered_at', { ascending: false });
      if (data) {
        setAlerts(data.map((row: any) => ({
          id: row.id,
          competitor_id: row.competitor_id,
          competitor_name: row.competitor_name,
          city: row.city,
          state: row.state,
          tier: row.tier as ThreatTier,
          message: row.message,
          triggered_at: row.triggered_at,
          dismissed: row.dismissed,
        })));
      }
    };
    loadAlerts();
  }, []);

  // ─── Re-rank whenever competitors list changes ────────────────────────────
  useEffect(() => {
    if (!competitors.length) {
      setRanked([]);
      return;
    }

    const newRanked = rankCompetitors(competitors);
    setRanked(newRanked);

    // Detect newly added high/critical competitors → fire red alerts
    const newHighThreats = newRanked.filter(
      (c) => (c.threatTier === 'critical' || c.threatTier === 'high') && !prevIdsRef.current.has(c.id)
    );

    if (newHighThreats.length > 0) {
      const newAlerts: ThreatAlert[] = newHighThreats.map((c) => ({
        id: `alert_${c.id}_${Date.now()}`,
        competitor_id: c.id,
        competitor_name: c.name,
        city: c.city,
        state: c.state,
        tier: c.threatTier,
        message: `${TIER_CONFIG[c.threatTier].label} competitor entered tracked market in ${c.city}, ${c.state}. Threat score: ${c.threatScore}/100. ${c.threatFactors[0] ?? ''}`,
        triggered_at: new Date().toISOString(),
        dismissed: false,
      }));

      setAlerts((prev) => {
        // Deduplicate by competitor_id — only one active alert per competitor
        const existingIds = new Set(prev.map((a) => a.competitor_id));
        const fresh = newAlerts.filter((a) => !existingIds.has(a.competitor_id));
        if (fresh.length === 0) return prev;

        // Persist to Supabase
        setSavingAlerts(true);
        supabase
          .from('expansion_threat_alerts')
          .upsert(
            fresh.map((a) => ({
              id: a.id,
              competitor_id: a.competitor_id,
              competitor_name: a.competitor_name,
              city: a.city,
              state: a.state,
              tier: a.tier,
              message: a.message,
              triggered_at: a.triggered_at,
              dismissed: false,
            })),
            { onConflict: 'competitor_id' }
          )
          .then(() => setSavingAlerts(false));

        return [...fresh, ...prev];
      });
    }

    // Update seen IDs
    prevIdsRef.current = new Set(competitors.map((c) => c.id));
  }, [competitors]);

  const handleDismiss = useCallback(async (alertId: string) => {
    setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, dismissed: true } : a)));
    await supabase.from('expansion_threat_alerts').update({ dismissed: true }).eq('id', alertId);
  }, [supabase]);

  const handleDismissAll = useCallback(async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, dismissed: true })));
    await supabase.from('expansion_threat_alerts').update({ dismissed: true }).eq('dismissed', false);
  }, [supabase]);

  const filtered = filterTier === 'all' ? ranked : ranked.filter((c) => c.threatTier === filterTier);

  if (competitors.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 text-center"
        style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1' }}
      >
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: '#F1F5F9' }}>
          <Icon name="ShieldCheckIcon" size={24} className="text-gray-400" />
        </div>
        <p className="text-sm font-semibold text-gray-500">No competitors tracked yet</p>
        <p className="text-xs text-gray-400 mt-1">Add competitors above to activate threat tier ranking</p>
      </div>
    );
  }

  return (
    <div>
      {/* ─── Red Alert Banner ───────────────────────────────────────────────── */}
      <AlertBanner alerts={alerts} onDismiss={handleDismiss} onDismissAll={handleDismissAll} />

      {/* ─── Tier Summary ───────────────────────────────────────────────────── */}
      <TierSummaryStrip ranked={ranked} />

      {/* ─── Filter Tabs ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {(['all', 'critical', 'high', 'medium', 'low'] as const).map((t) => {
          const isActive = filterTier === t;
          const cfg = t !== 'all' ? TIER_CONFIG[t] : null;
          return (
            <button
              key={t}
              onClick={() => setFilterTier(t)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{
                background: isActive ? (cfg?.color ?? '#1C2E5E') : '#F1F5F9',
                color: isActive ? '#fff' : '#64748B',
                border: `1px solid ${isActive ? (cfg?.color ?? '#1C2E5E') : '#E2E8F0'}`,
              }}
            >
              {t === 'all' ? `All (${ranked.length})` : `${TIER_CONFIG[t].label} (${ranked.filter((c) => c.threatTier === t).length})`}
            </button>
          );
        })}
        {savingAlerts && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-400">
            <div className="w-3 h-3 rounded-full border border-gray-300 border-t-transparent animate-spin" />
            Saving alerts…
          </div>
        )}
      </div>

      {/* ─── Ranked List ────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {filtered.map((c, idx) => {
          const cfg = TIER_CONFIG[c.threatTier];
          const isExpanded = expandedId === c.id;
          const isNew = alerts.some((a) => a.competitor_id === c.id && !a.dismissed);

          return (
            <div
              key={c.id}
              className="rounded-xl overflow-hidden transition-all"
              style={{
                border: `1.5px solid ${isNew ? '#DC2626' : cfg.border}`,
                background: '#fff',
                boxShadow: isNew ? '0 0 0 3px rgba(220,38,38,0.08)' : undefined,
              }}
            >
              {/* Row header */}
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : c.id)}
              >
                {/* Rank badge */}
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
                  style={{ background: cfg.bg, color: cfg.color }}
                >
                  #{idx + 1}
                </div>

                {/* Name + location */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900 truncate">{c.name}</span>
                    {isNew && (
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 animate-pulse">
                        NEW ALERT
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{c.city}, {c.state}</span>
                </div>

                {/* Tier badge */}
                <TierBadge tier={c.threatTier} />

                {/* Score bar */}
                <div className="w-28 hidden sm:block">
                  <ScoreBar score={c.threatScore} tier={c.threatTier} />
                </div>

                {/* Expand chevron */}
                <Icon
                  name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={14}
                  className="text-gray-400 flex-shrink-0"
                />
              </button>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: cfg.border, background: cfg.bg }}>
                  <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Threat factors */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: cfg.color }}>
                        Threat Factors
                      </p>
                      <ul className="space-y-1">
                        {c.threatFactors.map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                            <Icon name="CheckCircleIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: cfg.color } as any} />
                            {f}
                          </li>
                        ))}
                        {c.threatFactors.length === 0 && (
                          <li className="text-xs text-gray-400">No significant threat factors detected</li>
                        )}
                      </ul>
                    </div>

                    {/* Key metrics */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: cfg.color }}>
                        Key Metrics
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Threat Score</span>
                          <span className="font-black" style={{ color: cfg.color }}>{c.threatScore}/100</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Size</span>
                          <span className="font-semibold text-gray-800 capitalize">{c.size}</span>
                        </div>
                        {c.rating && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Rating</span>
                            <span className="font-semibold text-gray-800">{c.rating}★ ({c.reviewCount ?? 0} reviews)</span>
                          </div>
                        )}
                        {c.yearsInBusiness && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Years in Business</span>
                            <span className="font-semibold text-gray-800">{c.yearsInBusiness}</span>
                          </div>
                        )}
                        {c.marketShare && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Market Share</span>
                            <span className="font-semibold text-gray-800">{c.marketShare}%</span>
                          </div>
                        )}
                        {c.annualRevenue && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Annual Revenue</span>
                            <span className="font-semibold text-gray-800">{c.annualRevenue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tier description */}
                  <div
                    className="mt-3 rounded-lg px-3 py-2 text-xs"
                    style={{ background: 'rgba(255,255,255,0.6)', color: cfg.color }}
                  >
                    <Icon name="InformationCircleIcon" size={12} className="inline mr-1" />
                    {cfg.description}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
